<template>
  <div class="shop-search-page">
    <nav_list :index="4"></nav_list>

    <div class="search-wrap">
      <!-- ===== Initial Page Load Skeleton ===== -->
      <template v-if="initLoading">
        <div class="sk-search-bar"></div>
        <div class="sk-section-card">
          <div class="sk-section-header"></div>
          <div class="sk-tag-row">
            <div class="sk-tag" v-for="n in 6" :key="n"></div>
          </div>
        </div>
        <div class="sk-section-card">
          <div class="sk-section-header"></div>
          <div class="sk-hot-grid">
            <div class="sk-hot-item" v-for="n in 6" :key="n">
              <div class="sk-hot-rank"></div>
              <div class="sk-hot-text"></div>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
      <!-- Search Bar Card -->
      <div class="search-bar-card">
        <div class="search-bar-inner">
          <div class="back-btn" @click="goBack">
            <i class="el-icon-arrow-left"></i>
          </div>
          <div class="search-input-box">
            <i class="el-icon-search search-icon"></i>
            <input
              ref="searchInput"
              v-model="keyword"
              class="search-input"
              type="text"
              placeholder="搜索商品名称、关键词"
              @keyup.enter="doSearch"
            />
            <i
              v-if="keyword"
              class="el-icon-circle-close clear-icon"
              @click="clearKeyword"
            ></i>
          </div>
          <div class="search-btn" @click="doSearch">搜索</div>
        </div>
      </div>

      <!-- Before Search: History + Hot Keywords -->
      <template v-if="!hasSearched">
        <div v-if="searchHistory.length" class="section-card history-card">
          <div class="section-header">
            <span class="section-title">搜索历史</span>
            <span class="clear-history" @click="clearHistory">
              <i class="el-icon-delete"></i> 清空
            </span>
          </div>
          <div class="history-tags">
            <span
              class="history-tag"
              v-for="(item, index) in searchHistory"
              :key="index"
              @click="searchFromHistory(item)"
            >{{ item }}</span>
          </div>
        </div>

        <div class="section-card hot-card">
          <div class="section-header">
            <span class="section-title">热门搜索</span>
          </div>
          <div class="hot-list">
            <div
              class="hot-item"
              v-for="(item, index) in hotKeywords"
              :key="index"
              @click="searchFromHistory(item.keyword)"
            >
              <span class="hot-rank" :class="{ 'top': index < 3 }">{{ index + 1 }}</span>
              <span class="hot-text">{{ item.keyword }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- After Search: Results -->
      <template v-else>
        <div class="toolbar-box">
          <div class="category-nav" v-if="mainCategories.length > 1">
            <div class="cat-scroll main-cats">
              <div
                class="cat-item"
                v-for="cat in mainCategories"
                :key="'main-' + cat.id"
                :class="{ active: selectedMainCategoryId === cat.id }"
                @click="selectMainCategory(cat)"
              >{{ cat.name }}</div>
            </div>
            <div class="cat-scroll sub-cats" v-if="currentSubCategories.length">
              <div
                class="cat-item sub"
                :class="{ active: selectedSubCategoryId === 0 }"
                @click="selectSubCategory(0)"
              >全部</div>
              <div
                class="cat-item sub"
                v-for="sub in currentSubCategories"
                :key="'sub-' + sub.id"
                :class="{ active: selectedSubCategoryId === sub.id }"
                @click="selectSubCategory(sub.id)"
              >{{ sub.title }}</div>
            </div>
          </div>

          <div class="sort-bar">
            <div
              class="sort-item"
              :class="{ active: sortType === 'default' }"
              @click="changeSort('default')"
            >综合</div>
            <div
              class="sort-item"
              :class="{ active: sortType === 'sales' }"
              @click="changeSort('sales')"
            >销量</div>
            <div
              class="sort-item"
              :class="{ active: sortType === 'price' || sortType === 'price_desc' }"
              @click="togglePriceSort"
            >
              <span>价格</span>
              <span class="sort-arrows">
                <i class="el-icon-caret-top" :class="{ active: sortType === 'price' }"></i>
                <i class="el-icon-caret-bottom" :class="{ active: sortType === 'price_desc' }"></i>
              </span>
            </div>
            <div class="result-count">共 <strong>{{ total }}</strong> 件商品</div>
          </div>
        </div>

        <div class="product-box">
          <div v-if="productList.length" class="product-stage">
            <div class="product-grid" :class="{ 'is-loading': loading }">
              <div
                class="product-card"
                v-for="item in productList"
                :key="item.id"
                @click="goDetail(item)"
              >
                <div class="product-cover">
                  <img :src="item.image" :alt="item.name">
                  <span v-if="item.avg_scores && item.avg_scores !== '0'" class="score-tag">★ {{ item.avg_scores }}</span>
                </div>
                <div class="product-content">
                  <div class="product-title">{{ item.name }}</div>
                  <div class="product-footer">
                    <span class="product-price">¥ {{ item.price }}</span>
                    <span class="product-link">查看详情</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="loading" class="skeleton-overlay">
              <div class="skeleton-grid">
                <div v-for="n in (productList.length || pageSize)" :key="'sk-' + n" class="skeleton-card">
                  <div class="skeleton-cover"></div>
                  <div class="skeleton-content">
                    <div class="skeleton-line skeleton-line--title"></div>
                    <div class="skeleton-line skeleton-line--meta"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="loading" class="skeleton-grid">
            <div v-for="n in pageSize" :key="'sk-empty-' + n" class="skeleton-card">
              <div class="skeleton-cover"></div>
              <div class="skeleton-content">
                <div class="skeleton-line skeleton-line--title"></div>
                <div class="skeleton-line skeleton-line--meta"></div>
              </div>
            </div>
          </div>

          <el-empty v-else description="未找到相关商品"></el-empty>

          <div v-if="total > pageSize" class="pager-box" :class="{ 'is-loading': loading }">
            <el-pagination
              background
              layout="prev, pager, next"
              :current-page="page"
              :page-size="pageSize"
              :total="total"
              @current-change="changePage"
            />
          </div>
        </div>
      </template>

      <div v-if="loadFailed" class="error-box">
        <span>数据加载失败，请重试</span>
        <el-button type="primary" size="mini" @click="retrySearch">重新加载</el-button>
      </div>
      </template><!-- end v-else initLoading -->
    </div>
  </div>
</template>

<script>
import nav_list from '../index/nav_list.vue';

export default {
  name: 'shop-search',
  components: { nav_list },
  data() {
    return {
      keyword: '',
      autoFocus: true,
      hasSearched: false,
      productList: [],
      loading: false,
      hasMore: true,
      page: 1,
      pageSize: 24,
      total: 0,
      sortType: 'default',
      searchHistory: [],
      hotKeywords: [],
      mainCategories: [{ id: 0, name: '全部', children: [] }],
      selectedMainCategoryId: 0,
      selectedSubCategoryId: 0,
      selectedCategoryId: 0,
      pageLoading: false,
      initLoading: true,
      loadFailed: false,
    };
  },
  computed: {
    currentSubCategories() {
      const main = this.mainCategories.find(c => c.id === this.selectedMainCategoryId);
      return main && main.children ? main.children : [];
    },
  },
  mounted() {
    this.loadSearchHistory();
    this.loadHotKeywords();
    this.loadInitData();

    const queryKeyword = this.$route.query.keyword;
    if (queryKeyword) {
      this.keyword = queryKeyword;
    }
  },
  methods: {
    stripHtml(html) {
      if (!html) return '';
      return html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/&gt;/g, '>').replace(/&lt;/g, '<').replace(/&amp;/g, '&');
    },
    mapProduct(item) {
      return {
        id: item.prod_no,
        name: item.prod_name,
        image: item.prod_img_url && item.prod_img_url.length ? item.prod_img_url[0] : '',
        price: item.b2c_min_price || '0',
        avg_scores: item.avg_scores || '0',
      };
    },
    goBack() {
      this.back();
    },
    clearKeyword() {
      this.keyword = '';
      if (this.$refs.searchInput) {
        this.$refs.searchInput.focus();
      }
    },
    doSearch() {
      const kw = this.keyword.trim();
      if (!kw) {
        this.$message.error('请输入搜索关键词');
        return;
      }
      this.addHistory(kw);
      this.hasSearched = true;
      this.page = 1;
      this.productList = [];
      this.total = 0;
      this.searchProducts();
    },
    searchFromHistory(kw) {
      this.keyword = kw;
      this.doSearch();
    },
    loadSearchHistory() {
      try {
        this.searchHistory = JSON.parse(localStorage.getItem('shop_search_history')) || [];
      } catch (e) {
        this.searchHistory = [];
      }
    },
    addHistory(kw) {
      let history = this.searchHistory.filter(item => item !== kw);
      history.unshift(kw);
      if (history.length > 20) {
        history = history.slice(0, 20);
      }
      this.searchHistory = history.slice(0, 10);
      localStorage.setItem('shop_search_history', JSON.stringify(history));
    },
    clearHistory() {
      this.$confirm('确定清空搜索历史吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        this.searchHistory = [];
        localStorage.removeItem('shop_search_history');
      }).catch(() => {});
    },
    loadHotKeywords() {
      this.hotKeywords = [
        { keyword: '迪士尼' },
        { keyword: '海洋公园' },
        { keyword: '一日游' },
        { keyword: '门票' },
        { keyword: '景点门票' },
        { keyword: '观光巴士' },
      ];
    },
    async loadInitData() {
      this.initLoading = true;
      try {
        const configRes = await this.get('/kkday_product/home_config', {}, 'get', false);
        const categories = configRes.categories || [];
        this.mainCategories = [
          { id: 0, name: '全部', children: [] },
          ...categories.map(item => ({
            id: item.id,
            name: item.title,
            children: item.children || [],
          })),
        ];
      } catch (e) {
        // silently ignore
      } finally {
        this.initLoading = false;
        this.$nextTick(() => {
          if (this.keyword) {
            this.doSearch();
          } else if (this.autoFocus && this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
        });
      }
    },
    async searchProducts() {
      if (!this.keyword.trim()) return;
      this.loading = true;
      this.loadFailed = false;

      const params = {
        page: this.page,
        pageSize: this.pageSize,
        keyword: this.keyword.trim(),
      };

      if (this.sortType === 'sales') {
        params.orderby = 'sales_count';
        params.order = 'desc';
      } else if (this.sortType === 'price') {
        params.orderby = 'b2c_min_price';
        params.order = 'asc';
      } else if (this.sortType === 'price_desc') {
        params.orderby = 'b2c_min_price';
        params.order = 'desc';
      }

      if (this.selectedCategoryId !== 0) {
        params.cate_id = this.selectedCategoryId;
      }

      try {
        const res = await this.get('/kkday_product/list', params, 'get', false);
        this.productList = (res.list || []).map(this.mapProduct);
        this.total = Number(res.total || res.count || res.total_count || this.productList.length || 0);
        this.hasMore = this.page * this.pageSize < this.total;
      } catch (error) {
        this.productList = [];
        this.total = 0;
        this.loadFailed = true;
        this.$message.error('搜索失败，请重试');
      } finally {
        this.loading = false;
      }
    },
    selectMainCategory(cat) {
      this.selectedMainCategoryId = cat.id;
      this.selectedSubCategoryId = 0;

      if (cat.id === 0) {
        this.selectedCategoryId = 0;
      } else {
        this.selectedCategoryId = cat.id;
      }
      this.page = 1;
      this.searchProducts();
    },
    selectSubCategory(subId) {
      this.selectedSubCategoryId = subId;
      this.selectedCategoryId = subId || this.selectedMainCategoryId;
      this.page = 1;
      this.searchProducts();
    },
    changeSort(type) {
      if (this.sortType === type) return;
      this.sortType = type;
      this.page = 1;
      this.searchProducts();
    },
    togglePriceSort() {
      if (this.sortType === 'price') {
        this.sortType = 'price_desc';
      } else {
        this.sortType = 'price';
      }
      this.page = 1;
      this.searchProducts();
    },
    async changePage(page) {
      this.page = page;
      await this.searchProducts();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    retrySearch() {
      this.loadFailed = false;
      this.searchProducts();
    },
    goDetail(item) {
      this.jump('/shop/info', { prod_no: item.id, id: item.id });
    },
  },
};
</script>

<style scoped>
.shop-search-page {
  min-height: 100vh;
  background: #f5f7fb;
  padding-bottom: 40px;
}

.search-wrap {
  width: 1200px;
  margin: 20px auto 0;
}

/* ===== Search Bar ===== */
.search-bar-card,
.toolbar-box,
.product-box,
.section-card,
.error-box {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px 1px rgb(0 0 0 / 6%);
}

.search-bar-card {
  padding: 20px 28px;
}

.search-bar-inner {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #f5f7fb;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  color: #666;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.back-btn:hover {
  background: #eef3fb;
  color: #1c5098;
}

.search-input-box {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.search-input-box .search-icon {
  position: absolute;
  left: 16px;
  font-size: 18px;
  color: #bbb;
  z-index: 1;
}

.search-input-box .clear-icon {
  position: absolute;
  right: 14px;
  font-size: 18px;
  color: #bbb;
  cursor: pointer;
  z-index: 1;
  transition: color 0.2s;
}

.search-input-box .clear-icon:hover {
  color: #999;
}

.search-input {
  width: 100%;
  height: 48px;
  padding: 0 44px;
  border: 2px solid #e4e9f0;
  border-radius: 10px;
  font-size: 15px;
  color: #333;
  background: #f9fafb;
  outline: none;
  transition: all 0.25s ease;
}

.search-input:focus {
  border-color: #1c5098;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(28, 80, 152, 0.08);
}

.search-input::placeholder {
  color: #bbb;
}

.search-btn {
  height: 48px;
  padding: 0 32px;
  background: #1c5098;
  color: #fff;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s ease;
  letter-spacing: 0.05em;
}

.search-btn:hover {
  background: #164680;
}

/* ===== Before Search: History + Hot ===== */
.section-card {
  margin-top: 20px;
  padding: 24px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.section-title {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.clear-history {
  font-size: 13px;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.2s;
}

.clear-history:hover {
  color: #1c5098;
}

.history-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.history-tag {
  padding: 8px 18px;
  background: #f5f7fb;
  border-radius: 18px;
  font-size: 13px;
  color: #555;
  cursor: pointer;
  transition: all 0.2s ease;
}

.history-tag:hover {
  background: #eef3fb;
  color: #1c5098;
}

.hot-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px 24px;
}

.hot-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s;
}

.hot-item:hover {
  background: #f5f7fb;
}

.hot-rank {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: #edf1f7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: bold;
  color: #999;
  flex-shrink: 0;
}

.hot-rank.top {
  background: #1c5098;
  color: #fff;
}

.hot-text {
  font-size: 14px;
  color: #444;
}

/* ===== Toolbar: Sort + Categories ===== */
.toolbar-box {
  margin-top: 20px;
  padding: 0;
  overflow: hidden;
}

.category-nav {
  padding: 20px 24px 0;
}

.cat-scroll {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.cat-scroll::-webkit-scrollbar {
  height: 4px;
}

.cat-scroll::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 2px;
}

.sub-cats {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #edf1f7;
}

.cat-item {
  padding: 8px 18px;
  border-radius: 18px;
  background: #f5f7fb;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.cat-item:hover {
  background: #eef3fb;
  color: #1c5098;
}

.cat-item.active {
  background: #1c5098;
  color: #fff;
}

.cat-item.sub {
  padding: 6px 14px;
  font-size: 12px;
}

.sort-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 16px 24px;
  border-top: 1px solid #edf1f7;
  margin-top: 16px;
}

.sort-item {
  padding: 8px 20px;
  border-radius: 18px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
}

.sort-item:hover {
  color: #1c5098;
}

.sort-item.active {
  background: #1c5098;
  color: #fff;
}

.sort-arrows {
  display: flex;
  flex-direction: column;
  line-height: 1;
  margin-left: 2px;
}

.sort-arrows i {
  font-size: 10px;
  line-height: 1;
  color: #ccc;
  transition: color 0.2s;
}

.sort-arrows i.active {
  color: #fff;
}

.sort-item.active .sort-arrows i {
  color: rgba(255, 255, 255, 0.5);
}

.sort-item.active .sort-arrows i.active {
  color: #fff;
}

.result-count {
  margin-left: auto;
  font-size: 13px;
  color: #999;
}

.result-count strong {
  color: #1c5098;
  font-size: 15px;
}

/* ===== Product Grid ===== */
.product-box {
  margin-top: 20px;
  padding: 24px;
}

.product-stage {
  position: relative;
  min-height: 580px;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  transition: opacity 0.25s ease;
}

.product-grid.is-loading {
  opacity: 0.22;
  pointer-events: none;
}

.product-card {
  border: 1px solid #edf1f7;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
  transition: box-shadow 0.2s ease;
}

.product-card:hover {
  box-shadow: 0 6px 20px 2px rgb(0 0 0 / 10%);
}

.product-cover {
  position: relative;
  height: 220px;
  background: #f3f6fb;
}

.product-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.score-tag {
  position: absolute;
  right: 12px;
  top: 12px;
  padding: 4px 10px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.58);
  color: #fff;
  font-size: 12px;
}

.product-content {
  padding: 14px;
}

.product-title {
  color: #333;
  font-size: 15px;
  font-weight: bold;
  line-height: 1.7;
  min-height: 50px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-footer {
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.product-price {
  color: #1c5098;
  font-size: 22px;
  font-weight: bold;
}

.product-link {
  color: #8b95a7;
  font-size: 12px;
}

/* ===== Pagination ===== */
.pager-box {
  padding-top: 28px;
  text-align: right;
  transition: opacity 0.25s ease;
}

.pager-box.is-loading {
  opacity: 0.7;
}

/* ===== Skeleton ===== */
@keyframes sk-pulse {
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
}

.sk-search-bar,
.sk-section-card,
.sk-hot-grid {
  animation: sk-pulse 1.8s ease-in-out infinite;
}

.sk-search-bar {
  height: 88px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px 1px rgb(0 0 0 / 6%);
  margin-bottom: 20px;
}

.sk-section-card {
  margin-top: 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px 1px rgb(0 0 0 / 6%);
  padding: 24px;
}

.sk-section-header {
  width: 120px;
  height: 24px;
  border-radius: 6px;
  background: #e2e8f0;
  margin-bottom: 18px;
}

.sk-tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.sk-tag {
  width: 80px;
  height: 36px;
  border-radius: 18px;
  background: #e2e8f0;
}

.sk-hot-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px 24px;
}

.sk-hot-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
}

.sk-hot-rank {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: #e2e8f0;
  flex-shrink: 0;
}

.sk-hot-text {
  flex: 1;
  height: 16px;
  border-radius: 4px;
  background: #e2e8f0;
}

.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  min-height: 580px;
}

.skeleton-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
}

.skeleton-card {
  border: 1px solid #edf1f7;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.skeleton-cover,
.skeleton-line {
  position: relative;
  overflow: hidden;
  background: #eef3fb;
}

.skeleton-cover::after,
.skeleton-line::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.75), transparent);
  animation: skeleton-loading 1.4s infinite;
}

.skeleton-cover {
  height: 220px;
}

.skeleton-content {
  padding: 14px;
}

.skeleton-line {
  height: 14px;
  border-radius: 8px;
}

.skeleton-line--title {
  height: 42px;
  border-radius: 10px;
}

.skeleton-line--meta {
  width: 65%;
  margin-top: 14px;
}

@keyframes skeleton-loading {
  100% {
    transform: translateX(100%);
  }
}

/* ===== Error ===== */
.error-box {
  margin-top: 20px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

/* ===== Responsive ===== */
@media (max-width: 1260px) {
  .search-wrap {
    width: 96%;
  }

  .product-grid,
  .skeleton-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .hot-list,
  .sk-hot-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 900px) {
  .product-grid,
  .skeleton-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .hot-list,
  .sk-hot-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/shop/search.vue